import { useMemo, useState, useEffect, useCallback } from 'react';
import { TotalEditor } from './SeriesView';
import { loadTotals, saveTotals, findMissingTomes, formatRanges } from '../utils/seriesTotals';
import { countUniqueNums } from '../utils/groupUtils';
import { createShare, SHARE_RETENTION_DAYS } from '../utils/firestoreService';
import { useAuth } from '../hooks/useAuth';
import { renderShareCard, canvasToBlob } from '../utils/shareCard';
import { computeBadgeStats } from '../utils/badges';

const T = {
  orange:'var(--orange)', ink:'var(--ink)', bg:'var(--bg)', surface:'var(--surface)',
  border:'var(--border)', muted:'var(--muted)', subtle:'var(--subtle)', green:'#16A34A', red:'#DC2626',
};
const FONT    = "'Inter',-apple-system,BlinkMacSystemFont,sans-serif";
const DISPLAY = "'Bangers','Arial Black',cursive";

const inputStyle = {
  width:'100%', minWidth:0, padding:'9px 12px',
  border:`1.5px solid ${T.border}`, borderRadius:'9px',
  fontFamily:FONT, fontSize:'14px',
  background:T.surface, color:T.ink, outline:'none',
};
const segStyle = (active) => ({
  flex:1, padding:'9px 8px', border:`1.5px solid ${active?T.orange:T.border}`,
  borderRadius:'9px', background: active?'rgba(240,78,26,.08)':T.surface,
  color: active?T.orange:T.ink, fontFamily:FONT, fontSize:'13px', fontWeight:700, cursor:'pointer',
});
const chipToggle = (active) => ({
  padding:'6px 11px', borderRadius:'99px', border:`1.5px solid ${active?T.orange:T.border}`,
  background: active?T.orange:T.surface, color: active?'#fff':T.muted,
  fontFamily:FONT, fontSize:'12px', fontWeight:600, cursor:'pointer',
});

const FIELD_DEFS = [
  { key:'titre',    label:'Titre' },
  { key:'editeur',  label:'Éditeur' },
  { key:'auteur',   label:'Auteur' },
  { key:'etat',     label:'État' },
  { key:'prix',     label:"Prix d'achat" },
  { key:'statut',   label:'Statut' },
];

export default function ShareModal({ bds, onClose, onToast, initialScope=null, initialSerie=null, initialMode=null }) {
  const { user } = useAuth();
  const [totals, setTotals] = useState(loadTotals);
  const setTotal = (serie, val) => setTotals(t => {
    const next = { ...t };
    if (val === null) delete next[serie]; else next[serie] = val;
    saveTotals(next);
    return next;
  });

  const seriesList = useMemo(() => [...new Set(bds.map(b => b.serie).filter(Boolean))].sort((a,b)=>a.localeCompare(b,'fr')), [bds]);

  const [scope, setScope] = useState(initialScope || (seriesList.length ? 'serie' : 'all')); // 'serie' | 'all'
  const [serie, setSerie] = useState(initialSerie || seriesList[0] || '');
  const [mode, setMode]   = useState(initialMode || 'possede'); // 'possede' | 'manquant'
  const [fields, setFields] = useState({ titre:true, editeur:false, auteur:false, etat:false, prix:false, statut:false });
  const toggleField = k => setFields(f => ({ ...f, [k]: !f[k] }));

  const statutLabel = s => ({ garder:'À garder', vendre:'À vendre', pret:'Prêté', echange:'À échanger', don:'À donner' }[s] || s);

  const lineFor = useCallback(bd => {
    const parts = [`T.${bd.numero || '?'}`];
    if (fields.titre && bd.titre) parts.push(bd.titre);
    const extra = [];
    if (fields.editeur && bd.editeur) extra.push(bd.editeur);
    if (fields.auteur && bd.auteur) extra.push(bd.auteur);
    if (fields.etat && bd.etatGeneral) extra.push(bd.etatGeneral);
    if (fields.prix && bd.prixAchat) extra.push(`${bd.prixAchat}€`);
    if (fields.statut && bd.statut) extra.push(statutLabel(bd.statut));
    return extra.length ? `${parts.join(' — ')} (${extra.join(', ')})` : parts.join(' — ');
  }, [fields]);

  const { text, items, count, needsTotal, list } = useMemo(() => {
    const bySerie = {};
    bds.forEach(b => { const k = b.serie?.trim(); if (k) (bySerie[k] = bySerie[k] || []).push(b); });
    Object.values(bySerie).forEach(l => l.sort((a,b)=>(parseInt(a.numero)||0)-(parseInt(b.numero)||0)));

    const itemFor = (bd, s) => {
      const it = { serie: s, numero: bd.numero || '' };
      if (fields.titre && bd.titre) it.titre = bd.titre;
      if (fields.editeur && bd.editeur) it.editeur = bd.editeur;
      if (fields.auteur && bd.auteur) it.auteur = bd.auteur;
      if (fields.etat && bd.etatGeneral) it.etat = bd.etatGeneral;
      if (fields.prix && bd.prixAchat) it.prix = bd.prixAchat;
      if (fields.statut && bd.statut) it.statut = statutLabel(bd.statut);
      return it;
    };

    if (scope === 'serie') {
      if (!serie) return { text:'', items:[], count:0, needsTotal:false, list:[] };
      const l = bySerie[serie] || [];
      if (mode === 'possede') {
        const lines = l.map(lineFor);
        const uniq = countUniqueNums(l);
        const horsSerie = l.filter(bd => !String(bd.numero||'').trim()).length;
        const header = uniq === l.length
          ? `${l.length} tome${l.length>1?'s':''}`
          : `${uniq} tome${uniq>1?'s':''} (${l.length} album${l.length>1?'s':''} au total${horsSerie?`, dont ${horsSerie} hors-série${horsSerie>1?'s':''}`:''})`;
        return {
          text: `📚 Ma collection — ${serie} (${header})\n\n${lines.join('\n')}`,
          items: l.map(bd => itemFor(bd, serie)),
          count: l.length, needsTotal:false, list:l,
        };
      } else {
        const total = totals[serie] || null;
        if (!total) return { text:'', items:[], count:0, needsTotal:true, list:l };
        const missing = findMissingTomes(l, total);
        return {
          text: missing.length
            ? `📕 Tomes manquants — ${serie} (${missing.length})\n\n${formatRanges(missing).split(', ').map(r=>'T.'+r).join(', ')}`
            : `✓ ${serie} : collection complète (${total} tomes) !`,
          items: missing.map(n => ({ serie, numero: String(n) })),
          count: missing.length, needsTotal:false, list:l,
        };
      }
    } else {
      if (mode === 'possede') {
        const groups = Object.keys(bySerie).sort((a,b)=>a.localeCompare(b,'fr'));
        const blocks = groups.map(s => `— ${s} —\n${bySerie[s].map(lineFor).join('\n')}`);
        return {
          text: `📚 Ma collection complète (${bds.length} albums)\n\n${blocks.join('\n\n')}`,
          items: groups.flatMap(s => bySerie[s].map(bd => itemFor(bd, s))),
          count: bds.length, needsTotal:false, list:[],
        };
      } else {
        const rows = Object.entries(bySerie)
          .map(([s, l]) => ({ s, missing: findMissingTomes(l, totals[s] || null) }))
          .filter(x => x.missing.length)
          .sort((a,b)=>a.s.localeCompare(b.s,'fr'));
        const total = rows.reduce((n,x)=>n+x.missing.length,0);
        const body = rows.map(x => `${x.s} : ${formatRanges(x.missing)}`).join('\n');
        return {
          text: total ? `📕 Tomes manquants — toutes séries (${total})\n\n${body}` : '✓ Aucun tome manquant (séries avec total renseigné).',
          items: rows.flatMap(x => x.missing.map(n => ({ serie:x.s, numero:String(n) }))),
          count: total, needsTotal:false, list:[],
        };
      }
    }
  }, [bds, scope, serie, mode, totals, lineFor, fields]);

  const [edited, setEdited] = useState(null);
  const finalText = edited !== null ? edited : text;
  // Repart de la génération à chaque changement de critère
  useEffect(() => { setEdited(null); }, [scope, serie, mode, fields]);

  const handleShare = async () => {
    if (!finalText) return;
    if (navigator.share) {
      try { await navigator.share({ text: finalText }); }
      catch (e) { if (e.name !== 'AbortError') onToast?.('Partage impossible sur cet appareil', 'error'); }
    } else {
      onToast?.('Partage natif indisponible ici — utilise Copier ou SMS', 'error');
    }
  };
  const handleSMS = () => {
    if (!finalText) return;
    window.location.href = 'sms:?body=' + encodeURIComponent(finalText);
  };
  const handleCopy = async () => {
    if (!finalText) return;
    try { await navigator.clipboard.writeText(finalText); onToast?.('Texte copié ✓'); }
    catch { onToast?.('Copie impossible sur ce navigateur', 'error'); }
  };

  const [link, setLink] = useState(null);
  const [creatingLink, setCreatingLink] = useState(false);
  // Un nouveau clic sur "Créer un lien" avec des critères différents doit repartir de zéro
  useEffect(() => { setLink(null); }, [scope, serie, mode, fields]);

  const linkTitle = scope==='serie'
    ? (mode==='possede' ? `Collection — ${serie}` : `Tomes manquants — ${serie}`)
    : (mode==='possede' ? 'Ma collection complète' : 'Tomes manquants — toutes séries');

  const handleCreateLink = async () => {
    if (!items.length) return;
    setCreatingLink(true);
    try {
      // Le lien public n'inclut jamais le prix ni le statut (vente/échange),
      // même si ces cases sont cochées pour le SMS — c'est un lien sans mot
      // de passe, accessible par n'importe qui.
      const publicItems = items.map(({ prix, statut, ...rest }) => rest);
      const shareId = await createShare(user.uid, { title: linkTitle, items: publicItems });
      setLink(`${window.location.origin}/partage/${shareId}`);
    } catch(e) { onToast?.('Erreur : '+e.message, 'error'); }
    setCreatingLink(false);
  };
  const handleCopyLink = async () => {
    if (!link) return;
    try { await navigator.clipboard.writeText(link); onToast?.('Lien copié ✓'); }
    catch { onToast?.('Copie impossible sur ce navigateur', 'error'); }
  };

  // ── Image partageable (carte façon story Instagram/Snap) ───────────────
  const [cardCanvas, setCardCanvas] = useState(null);
  const [cardUrl, setCardUrl]       = useState(null);
  const [cardBusy, setCardBusy]     = useState(false);
  useEffect(() => { setCardCanvas(null); setCardUrl(null); }, [scope, serie, mode, totals]);

  const cardReady = mode === 'manquant' ? count > 0 : (scope==='serie' ? list.length>0 : bds.length>0);

  const handleGenerateCard = async () => {
    if (!cardReady) return;
    setCardBusy(true);
    try {
      let kind, data;
      if (mode === 'manquant') {
        kind = 'missing';
        data = { serie: scope==='serie' ? serie : null, missing: count };
      } else if (scope === 'serie') {
        const total = totals[serie] || 0;
        const owned = countUniqueNums(list);
        const horsSerie = list.filter(bd => !String(bd.numero||'').trim()).length;
        kind = 'serie';
        data = { serie, owned, total, horsSerie, complete: total ? findMissingTomes(list, total).length===0 : false };
      } else {
        const stats = computeBadgeStats(bds, totals);
        kind = 'collection';
        data = { total: bds.length, series: seriesList.length, complete: stats.seriesCompletes };
      }
      const canvas = await renderShareCard(kind, data);
      setCardCanvas(canvas);
      setCardUrl(canvas.toDataURL('image/png'));
    } catch(e) { onToast?.('Erreur lors de la génération : '+(e.message||e), 'error'); }
    setCardBusy(false);
  };

  const handleDownloadCard = () => {
    if (!cardUrl) return;
    const a = document.createElement('a');
    a.href = cardUrl; a.download = 'ma-collection-bulletheque.png';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };
  const handleShareCard = async () => {
    if (!cardCanvas) return;
    const blob = await canvasToBlob(cardCanvas);
    const file = new File([blob], 'ma-collection-bulletheque.png', { type:'image/png' });
    if (navigator.canShare?.({ files:[file] })) {
      try { await navigator.share({ files:[file] }); }
      catch(e) { if (e.name !== 'AbortError') onToast?.('Partage impossible sur cet appareil', 'error'); }
    } else {
      onToast?.("Le partage direct de l'image n'est pas dispo ici — télécharge-la puis partage-la manuellement.", 'error');
    }
  };
  const handleCopyCardImage = async () => {
    if (!cardCanvas) return;
    try {
      const blob = await canvasToBlob(cardCanvas);
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      onToast?.('Image copiée ✓');
    } catch { onToast?.("Copie d'image non supportée sur ce navigateur", 'error'); }
  };

  const btn = (primary) => ({
    flex:1, padding:'11px 8px', borderRadius:'11px', fontFamily:FONT, fontSize:'13px', fontWeight:700,
    cursor:'pointer', border: primary ? 'none' : `1.5px solid ${T.border}`,
    background: primary ? T.orange : T.surface, color: primary ? '#fff' : T.ink,
  });

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(28,22,18,.6)',display:'flex',alignItems:'flex-end',justifyContent:'center',zIndex:1000,backdropFilter:'blur(6px)',WebkitBackdropFilter:'blur(6px)'}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:T.bg,borderRadius:'22px 22px 0 0',border:`1px solid ${T.border}`,borderBottom:'none',width:'100%',maxWidth:'640px',maxHeight:'92vh',overflowY:'auto',overflowX:'hidden',fontFamily:FONT,boxShadow:'0 -12px 40px rgba(0,0,0,.18)'}}>
        <div style={{position:'sticky',top:0,zIndex:10,background:'var(--header-bg)',backdropFilter:'blur(10px)',borderBottom:`1px solid ${T.border}`,padding:'1rem 1.25rem',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div style={{fontFamily:DISPLAY,fontSize:'1.35rem',letterSpacing:'.05em',color:T.orange}}>📤 Partager</div>
            <div style={{fontSize:'12px',color:T.muted,marginTop:'1px'}}>Envoie ta liste par SMS ou autre appli</div>
          </div>
          <button onClick={onClose} style={{width:'32px',height:'32px',borderRadius:'50%',border:`1px solid ${T.border}`,background:T.surface,cursor:'pointer',fontSize:'16px',color:T.muted}}>×</button>
        </div>

        <div style={{padding:'1.25rem',display:'flex',flexDirection:'column',gap:'14px'}}>
          {/* Portée */}
          <div>
            <div style={{fontSize:'11px',fontWeight:700,color:T.muted,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:'6px'}}>Quoi</div>
            <div style={{display:'flex',gap:'8px'}}>
              <button style={segStyle(scope==='serie')} onClick={()=>setScope('serie')} disabled={!seriesList.length}>Une série</button>
              <button style={segStyle(scope==='all')} onClick={()=>setScope('all')}>Toute ma collection</button>
            </div>
          </div>

          {scope==='serie' && (
            <select style={inputStyle} value={serie} onChange={e=>setSerie(e.target.value)}>
              {seriesList.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          )}

          {/* Mode */}
          <div>
            <div style={{fontSize:'11px',fontWeight:700,color:T.muted,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:'6px'}}>Contenu</div>
            <div style={{display:'flex',gap:'8px'}}>
              <button style={segStyle(mode==='possede')} onClick={()=>setMode('possede')}>Que je possède</button>
              <button style={segStyle(mode==='manquant')} onClick={()=>setMode('manquant')}>Qui me manque</button>
            </div>
          </div>

          {/* Total nécessaire pour le mode manquant */}
          {mode==='manquant' && scope==='serie' && needsTotal && (
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px',background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:'9px',padding:'10px 12px'}}>
              <div style={{fontSize:'12px',fontWeight:600,color:'#92400E'}}>Indique le nombre total de tomes de la série pour détecter les manquants.</div>
              <TotalEditor serie={serie} value={totals[serie]||null} onChange={v=>setTotal(serie,v)} />
            </div>
          )}

          {/* Champs à inclure (uniquement pertinent en mode "possède") */}
          {mode==='possede' && (
            <div>
              <div style={{fontSize:'11px',fontWeight:700,color:T.muted,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:'6px'}}>Infos à inclure</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:'6px'}}>
                {FIELD_DEFS.map(f=>(
                  <button key={f.key} style={chipToggle(fields[f.key])} onClick={()=>toggleField(f.key)}>{f.label}</button>
                ))}
              </div>
            </div>
          )}

          {/* Aperçu */}
          <div>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'6px'}}>
              <div style={{fontSize:'11px',fontWeight:700,color:T.muted,textTransform:'uppercase',letterSpacing:'.05em'}}>Aperçu {count>0 && `(${count})`}</div>
              {finalText.length>600 && <div style={{fontSize:'11px',color:T.subtle}}>{finalText.length} caractères — plutôt pour WhatsApp/Mail qu'un SMS classique</div>}
            </div>
            <textarea value={finalText} onChange={e=>setEdited(e.target.value)} rows={8}
              style={{width:'100%',minWidth:0,padding:'10px 12px',border:`1.5px solid ${T.border}`,borderRadius:'9px',fontFamily:FONT,fontSize:'13px',lineHeight:1.5,background:T.surface,color:T.ink,outline:'none',resize:'vertical'}}/>
          </div>

          {/* Lien web en lecture seule */}
          <div>
            <div style={{fontSize:'11px',fontWeight:700,color:T.muted,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:'6px'}}>Lien web (sans compte)</div>
            {!link ? (
              <button onClick={handleCreateLink} disabled={creatingLink||!items.length} style={btn(false)}>
                {creatingLink ? 'Création…' : '🔗 Créer un lien à partager'}
              </button>
            ) : (
              <div style={{display:'flex',alignItems:'center',gap:'8px',background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:'9px',padding:'9px 10px'}}>
                <div style={{flex:1,minWidth:0,fontSize:'12.5px',color:'#15803D',fontWeight:600,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{link}</div>
                <button onClick={handleCopyLink} style={{border:'none',background:'#16A34A',color:'#fff',borderRadius:'7px',padding:'6px 10px',fontFamily:FONT,fontSize:'12px',fontWeight:700,cursor:'pointer',flexShrink:0}}>Copier</button>
              </div>
            )}
            <div style={{fontSize:'11px',color:T.subtle,marginTop:'5px',lineHeight:1.4}}>
              Ouvrable par n'importe qui, sans compte, valable {SHARE_RETENTION_DAYS} jours puis expire automatiquement. Le prix et le statut (vente/échange) ne sont jamais inclus dans ce lien, même si tu les as cochés ci-dessus. Un nouveau lien est créé à chaque fois (l'ancien reste valable tel quel).
            </div>
          </div>

          {/* Image partageable (Instagram / Snap) */}
          <div>
            <div style={{fontSize:'11px',fontWeight:700,color:T.muted,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:'6px'}}>Image à partager</div>
            {!cardUrl ? (
              <button onClick={handleGenerateCard} disabled={cardBusy||!cardReady} style={btn(false)}>
                {cardBusy ? 'Génération…' : '🖼️ Créer une carte visuelle'}
              </button>
            ) : (
              <div>
                <div style={{display:'flex',justifyContent:'center',background:'#F3ECE4',borderRadius:'12px',padding:'10px',marginBottom:'8px'}}>
                  <img src={cardUrl} alt="Carte de collection" style={{width:'140px',borderRadius:'8px',boxShadow:'0 6px 18px rgba(0,0,0,.18)'}}/>
                </div>
                <div style={{display:'flex',gap:'8px'}}>
                  <button onClick={handleDownloadCard} style={{...btn(false),flex:1}}>⬇ Télécharger</button>
                  <button onClick={handleCopyCardImage} style={{...btn(false),flex:1}}>📋 Copier</button>
                  <button onClick={handleShareCard} style={{...btn(true),flex:1}}>📤 Partager</button>
                </div>
              </div>
            )}
            <div style={{fontSize:'11px',color:T.subtle,marginTop:'5px',lineHeight:1.4}}>
              Format story (1080×1920), prête pour Instagram ou Snapchat.
            </div>
          </div>

          <div style={{display:'flex',gap:'8px'}}>
            <button onClick={handleCopy} style={btn(false)}>📋 Copier</button>
            <button onClick={handleSMS} style={btn(false)}>💬 SMS</button>
            <button onClick={handleShare} style={btn(true)}>📤 Partager…</button>
          </div>
        </div>
      </div>
    </div>
  );
}
